import { useState, useEffect, useMemo, useCallback } from 'react'
import { lookupInBrowserByKey } from '@/utilities/browserLookup'

/**
 * Key used to store the impersonated user ID in localStorage
 */
export const IMPERSONATION_STORAGE_KEY = 'userImpersonation'

/**
 * Helper function to safely get value from lookupInBrowserByKey
 */
const safeGetStorageValue = (key: string): string | null => {
  try {
    return lookupInBrowserByKey(key)
  } catch {
    return null
  }
}

/**
 * Helper function to check if a user ID is valid
 */
const isValidUserId = (userId: string | null): boolean => {
  if (!userId || userId.trim() === '') {
    return false
  }

  const trimmedId = userId.trim()

  // Minimum reasonable length for user ID
  if (trimmedId.length < 10) {
    console.warn(
      '[Impersonation] Impersonation ID appears invalid (too short):',
      trimmedId.length
    )
    return false
  }

  // Check if ID contains only valid characters (alphanumeric, hyphens, underscores)
  if (!/^[a-zA-Z0-9\-_]+$/.test(trimmedId)) {
    console.warn('[Impersonation] Impersonation ID contains invalid characters')
    return false
  }

  return true
}

/**
 * Helper function to check if user has an active session
 */
const hasActiveSession = (): boolean => {
  try {
    return Boolean(
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      localStorage.getItem('aws-cognito-identity-id') ||
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        sessionStorage.getItem('aws-cognito-identity-id') ||
        Object.keys(localStorage).some(key =>
          key.startsWith('CognitoIdentityServiceProvider')
        )
    )
  } catch (error) {
    console.warn('[Impersonation] Could not validate session:', error)
    return false
  }
}

/**
 * Helper function to safely clear localStorage
 */
const safeClearStorage = (key: string): boolean => {
  try {
    localStorage.removeItem(key)
    return true
  } catch (error) {
    console.warn('[Impersonation] Failed to clear storage:', error)
    return false
  }
}

/**
 * Helper function to safely set localStorage
 */
const safeSetStorage = (key: string, value: string): boolean => {
  try {
    localStorage.setItem(key, value)
    return true
  } catch (error) {
    console.warn('[Impersonation] Failed to set storage:', error)
    return false
  }
}

/**
 * Helper function to dispatch custom storage change event
 */
const dispatchStorageChangeEvent = (): void => {
  try {
    window.dispatchEvent(new CustomEvent('userImpersonationChanged'))
  } catch (error) {
    console.warn('[Impersonation] Failed to dispatch storage event:', error)
  }
}

/**
 * Custom hook for managing user impersonation state
 * Provides memoized validation logic and live updates when localStorage changes
 */
export const useUserImpersonation = () => {
  const [storageValue, setStorageValue] = useState<string | null>(() =>
    safeGetStorageValue(IMPERSONATION_STORAGE_KEY)
  )

  // Memoized validation logic
  const isImpersonating = useMemo((): boolean => {
    if (!isValidUserId(storageValue)) {
      return false
    }

    if (!hasActiveSession()) {
      console.warn(
        '[Impersonation] Impersonation detected but no active session found'
      )
      return false
    }

    return true
  }, [storageValue])

  // Memoized impersonated user ID getter
  const impersonatedUserId = useMemo((): string | null => {
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    return storageValue?.trim() || null
  }, [storageValue])

  // Update storage value when localStorage changes
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === IMPERSONATION_STORAGE_KEY) {
        setStorageValue(e.newValue)
      }
    }

    const handleCustomStorageChange = () => {
      const newValue = safeGetStorageValue(IMPERSONATION_STORAGE_KEY)
      setStorageValue(newValue)
    }

    // Listen for storage events from other tabs/windows
    window.addEventListener('storage', handleStorageChange)

    // Listen for custom storage events for same-tab changes
    window.addEventListener(
      'userImpersonationChanged',
      handleCustomStorageChange
    )

    return () => {
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener(
        'userImpersonationChanged',
        handleCustomStorageChange
      )
    }
  }, [storageValue])

  // Utility functions
  const clearImpersonation = useCallback((): void => {
    // Clear storage and state first
    safeClearStorage(IMPERSONATION_STORAGE_KEY)
    setStorageValue(null)

    // Handle URL cleanup
    try {
      const url = new URL(window.location.href)
      if (url.searchParams.has('userImpersonation')) {
        url.searchParams.delete('userImpersonation')
        window.history.replaceState({}, '', url.toString())
      }
    } catch (error) {
      console.warn('[Impersonation] Failed to clean URL:', error)
    }

    // Dispatch custom event for same-tab listeners
    dispatchStorageChangeEvent()

    // Refresh to ensure clean state
    try {
      window.location.reload()
    } catch (error) {
      console.warn('[Impersonation] Failed to reload page:', error)
    }
  }, [])

  const setImpersonation = useCallback(
    (userId: string): void => {
      if (!userId || userId.trim() === '') {
        clearImpersonation()
        return
      }

      const trimmedUserId = userId.trim()
      const success = safeSetStorage(IMPERSONATION_STORAGE_KEY, trimmedUserId)

      if (success) {
        setStorageValue(trimmedUserId)
        dispatchStorageChangeEvent()
      } else {
        // Ensure state is cleared on error
        setStorageValue(null)
      }
    },
    [clearImpersonation]
  )

  return {
    isImpersonating,
    impersonatedUserId,
    clearImpersonation,
    setImpersonation,
  }
}
